import type { ReactNode } from 'react'
import { Platform } from 'react-native'
import { usePathname } from 'expo-router'

import { BaseModal } from '../ui/BaseModal'
import { AgeVerificationPrompt } from '../../src/ageGate/components/AgeVerificationPrompt/AgeVerificationPrompt'
import { useAgeVerification } from '../../src/ageGate/hooks/useAgeVerification'
import { useAgeGateStore } from '../../src/ageGate/stores/ageGateStore'
import { AGE_GATE_MODAL_SUPPRESSED_PATHS } from '../../src/ageGate/constants/ageGate.constants'
import { ageVerificationPromptStyles } from '../../src/ageGate/components/AgeVerificationPrompt/AgeVerificationPrompt.styles'

/**
 * 18+ age gate for authenticated routes (specs/coin-compliance).
 *
 * Children are ALWAYS rendered; the prompt sits on top in a modal. Replacing children with
 * another screen unmounts the Tabs navigator and drops the route a new guest is heading to
 * (see AccountDeletionAccessGate), so this gate never does that.
 *
 * Hidden on the tutorial and the onboarding questionnaire: there the age question is a step of
 * the flow (specs/onboarding-questionnaire Story 7). Anywhere else it still catches the user.
 */
export function AgeVerificationGate({ children }: { children: ReactNode }) {
  const { status, isSubmitting, submitError, submit, signOut } = useAgeVerification()
  const pathname = usePathname()
  const isSuppressed = AGE_GATE_MODAL_SUPPRESSED_PATHS.includes(pathname)
  const statusUserId = useAgeGateStore((s) => s.userId)
  const isVisible = !isSuppressed && (status === 'required' || status === 'blocked')

  return (
    <>
      {children}
      {isVisible ? (
        <BaseModal
          // A fresh native Modal per account: after an account switch the prompt must be
          // presented anew, never inherit a presentation iOS refused for the previous user.
          key={statusUserId ?? 'no-user'}
          isVisible
          dismissible={false}
          onDismiss={() => undefined}
          contentAlignment="center"
          contentContainerStyle={ageVerificationPromptStyles.modalOuter}
          testID="age-verification-gate"
          // iOS: an in-app layer, never a native presentation — it must appear even when the
          // account switcher or the Apple/Google sign-in sheet is still closing (AC-5.5).
          renderInline={Platform.OS === 'ios'}
        >
          <AgeVerificationPrompt
            mode={status === 'blocked' ? 'blocked' : 'prompt'}
            isSubmitting={isSubmitting}
            submitError={submitError}
            onSubmit={(parts) => void submit(parts)}
            onSignOut={() => void signOut()}
          />
        </BaseModal>
      ) : null}
    </>
  )
}
